import { createStyles, Menu } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { IconHelpCircle, IconInfoCircle, IconOutbound, IconSettings } from '@tabler/icons-react';
import { useCallback, useContext } from 'react';
import { ContestType } from '@betterpool/api-types/contests-service';

import { Button } from '~/domains/design-system/Button';
import IconThreeDotsMenu from '~/domains/design-system/icons/IconThreeDotsMenu';
import { Text } from '~/domains/design-system/Text';
import { useTutorialContext } from '~/domains/tutorials/TutorialModalContext';
import dt from '~/testing';
import { RootModalsContext } from '~/domains/common/context/RootModalsContext/RootModalsContext';
import useGlossary from '~/domains/cms/glossaries/useGlossary';
import { GlossaryProp } from '~/domains/cms/enums/glossary.enum';
import { LeagueAlias } from '~/domains/common/enums/league-alias.enum';
import { UserContext } from '~/components/providers/UserProvider';

type PicksLayoutHeaderMenuProps = {
  onWithdrawEntry?: () => void;
  leagueAlias: LeagueAlias;
  contestType: ContestType;
  hasAutoPicksEnabled?: boolean;
  onEditAutoPicks?: () => void;
};
const useStyles = createStyles((theme) => ({
  threeDotsMenu: { marginLeft: theme.other.spacing._4 },
}));

function PicksLayoutHeaderMenu({
  onWithdrawEntry,
  leagueAlias,
  contestType,
  hasAutoPicksEnabled = false,
  onEditAutoPicks,
}: PicksLayoutHeaderMenuProps) {
  const { user } = useContext(UserContext);
  const { t } = useTranslation('contest');
  const { setIsOpen, tutorialSteps } = useTutorialContext();
  const { openModal } = useContext(RootModalsContext);
  const { classes, theme } = useStyles();

  const getGlossaryProp = () => {
    const combination = `${contestType}-${leagueAlias}`;

    switch (combination) {
      case `${ContestType.PLAYER_SURVIVOR}-${LeagueAlias.MLB}`:
        return GlossaryProp.HITS;
      case `${ContestType.PLAYER_SURVIVOR}-${LeagueAlias.NFL}`:
        return GlossaryProp.ANYTIME_TOUCHDOWNS;
      default:
        return null;
    }
  };

  const glossaryProp = getGlossaryProp();
  const shouldFetchGlossary = !!glossaryProp;

  const {
    data: glossaryData,
    isError: glossaryError,
    isLoading: isGlossaryLoading,
  } = useGlossary(
    leagueAlias,
    glossaryProp || GlossaryProp.HITS, // Fallback to avoid type issues
    shouldFetchGlossary
  );

  const openGlossary = useCallback(() => {
    openModal({
      type: 'GlossaryModal',
      league: leagueAlias,
      glossaryData,
    });
  }, [openModal, leagueAlias, glossaryData]);

  const openTutorial = () =>
    setIsOpen({ isOpen: true, onClose: () => setIsOpen({ isOpen: false }) });

  const shouldShowGlossary =
    shouldFetchGlossary &&
    glossaryData?.glossary?.length > 0 &&
    !glossaryError &&
    !isGlossaryLoading;

  const hasTutorialItem = tutorialSteps.length > 0;
  const hasAutoPicksItem = hasAutoPicksEnabled && onEditAutoPicks && !!user?.id;
  const hasWithdrawItem = onWithdrawEntry && !!user?.id;
  const hasMenuItems = hasTutorialItem || hasAutoPicksItem || hasWithdrawItem;

  return (
    <>
      {shouldShowGlossary && (
        <Button size="small-icon" variant="text" aria-label="Open glossary" onClick={openGlossary}>
          <IconHelpCircle color={theme.colors.gray[2]} />
        </Button>
      )}

      {hasMenuItems && (
        <Menu
          data-test-id={dt.contest.gamecenter.common.picksHeader.headerMenu.container}
          radius="md"
          position="bottom-end"
          width={200}
        >
          <Menu.Target>
            <Button size="small-icon" variant="text" className={classes.threeDotsMenu}>
              <IconThreeDotsMenu />
            </Button>
          </Menu.Target>

          <Menu.Dropdown>
            {hasTutorialItem && (
              <Menu.Item icon={<IconInfoCircle size={16} />} onClick={openTutorial}>
                <Text variant="subhead-medium">
                  {t('gamecenter.common.picksHeader.headerMenu.tutorial')}
                </Text>
              </Menu.Item>
            )}

            {hasAutoPicksItem && (
              <Menu.Item icon={<IconSettings size={16} />} onClick={onEditAutoPicks}>
                <Text variant="subhead-medium">Auto-pick logic</Text>
              </Menu.Item>
            )}

            {hasWithdrawItem && (
              <Menu.Item
                data-test-id={dt.contest.gamecenter.common.picksHeader.headerMenu.withdraw}
                icon={<IconOutbound size={16} />}
                onClick={onWithdrawEntry}
              >
                <Text variant="subhead-medium">
                  {t('gamecenter.common.picksHeader.headerMenu.withdraw')}
                </Text>
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
      )}
    </>
  );
}

export default PicksLayoutHeaderMenu;
